import React from 'react';
import { Box, Heading, Label, Text, Icon } from '@medely/ui-kit';
import { gateNames } from '@mp/constants/statsig';
import { useGate } from '@mp/hooks';
import { JobDetailsListItem } from '../../JobDetailsListItem';
import { centsToCurrency } from '@mp/utils';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';
import { JobDetailsFragment } from '../graphql';

type OnsiteLogisticsProps = {
  job?: FragmentType<typeof JobDetailsFragment> | null;
  smallIcon?: boolean;
};

export function OnsiteLogistics({ job: inputJob, smallIcon = false }: OnsiteLogisticsProps) {
  const job = parseFragmentData(JobDetailsFragment, inputJob);

  const isNewFacilityGuideFormat = useGate(gateNames.facilityGuide);

  if (!isNewFacilityGuideFormat) {
    return null;
  }

  const bookedOrInProgressOrCompleted = ['booked', 'in_progress', 'completed'].includes(
    job?.status ?? '',
  );
  const hasScrubs = job?.scrubs_provided;
  const isParkingFree = job?.location?.parking_free;
  const isParkingReimbursed = job?.location?.parking_reimbursed;
  const isParkingValidated = job?.location?.parking_validated;
  const parkCost = centsToCurrency(job?.location?.parking_cost_cents ?? 0);
  const parkReimbursed = centsToCurrency(job?.location?.parking_reimbursement_cents ?? 0);
  const hasUnpaidBreaks = job?.track_unpaid_breaks;
  const breakDuration = job?.estimated_unpaid_break_time_minutes;

  const hasValidBreakDuration =
    breakDuration !== null && breakDuration !== undefined && breakDuration > 0;

  const scrubColors = job?.location?.scrub_colors;

  const mealBreakDetails = job?.location?.meal_break_details;

  const additionalCheckInInstructions = job?.location?.additional_check_in_instructions;

  const formatBreakDuration = (minutes: number) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${remainingMinutes} min`;
    }
    return `${minutes} min`;
  };

  return (
    <Box p={smallIcon ? 2 : 0}>
      <Heading size="s" color="text.primary" testId="onsite-logistics-title">
        Onsite logistics
      </Heading>

      {/* Dress code */}
      <Box mt={smallIcon ? 1 : 2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-tshirt"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          titleNode={
            <Label size="l" color="text.primary" testId="dress-code-heading">
              Dress code
            </Label>
          }
        >
          <Text size="m" color="text.secondary" testId="dress-code-content">
            •{' '}
            {hasScrubs
              ? 'The facility will provide scrubs'
              : 'Professionals should bring their own scrubs'}
          </Text>
          {scrubColors && scrubColors.length > 0 && (
            <Text size="m" color="text.secondary" testId="dress-code-acceptable-colors">
              • Acceptable colors:{' '}
              {scrubColors
                .map((color) => (color ? color.charAt(0).toUpperCase() + color.slice(1) : null))
                .join(', ')}
            </Text>
          )}
        </JobDetailsListItem>
      </Box>

      {/* Parking */}
      <Box mt={smallIcon ? 1 : 3}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-parking-circle"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          titleNode={
            <Label size="l" color="text.primary" testId="parking-heading">
              Parking
            </Label>
          }
        >
          <Text size="m" color="text.secondary" testId="parking-content">
            • {job?.location?.parking_instructions || 'Free parking'}
          </Text>
          {isParkingFree ? (
            <Text size="m" color="text.secondary" testId="parking-free">
              • Free parking
            </Text>
          ) : (
            <Text size="m" color="text.secondary" testId="parking-cost">
              • Parking cost ({parkCost})
            </Text>
          )}
          {isParkingReimbursed && (
            <Text size="m" color="text.secondary" testId="parking-reimbursed">
              • Parking reimbursed ({parkReimbursed})
            </Text>
          )}
          {isParkingValidated && (
            <Text size="m" color="text.secondary" testId="parking-validated">
              • Parking validated
            </Text>
          )}
        </JobDetailsListItem>
      </Box>

      {/* Clock in instructions - only for booked/in progress/completed */}
      {bookedOrInProgressOrCompleted && (
        <Box mt={smallIcon ? 1 : 3}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-clock"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            titleNode={
              <Label size="l" color="text.primary" testId="clock-in-instructions-heading">
                Clock in instructions
              </Label>
            }
          >
            {additionalCheckInInstructions ? (
              <Text size="m" color="text.secondary" testId="clock-in-instructions-content">
                • {additionalCheckInInstructions}
              </Text>
            ) : (
              <Text size="m" color="text.secondary" testId="clock-in-instructions-content">
                • Check-in instructions will be provided by the facility
              </Text>
            )}
          </JobDetailsListItem>
        </Box>
      )}

      {/* Meal breaks */}
      <Box mt={smallIcon ? 1 : 3}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-burger-soda"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          titleNode={
            <Label size="l" color="text.primary" testId="meal-breaks-heading">
              Meal breaks
            </Label>
          }
        >
          {/* Duration only shows for unpaid breaks */}
          {hasUnpaidBreaks && hasValidBreakDuration && (
            <Text size="m" color="text.secondary" testId="meal-breaks-duration">
              • Duration: {formatBreakDuration(breakDuration)}
            </Text>
          )}
          <Text size="m" color="text.secondary" testId="meal-breaks-type">
            • {hasUnpaidBreaks ? 'Unpaid meal breaks' : 'Paid meal breaks'}
          </Text>
          {mealBreakDetails && (
            <Text size="m" color="text.secondary" testId="meal-breaks-details">
              • {mealBreakDetails}
            </Text>
          )}
        </JobDetailsListItem>
      </Box>
    </Box>
  );
}
